import { Link } from "react-router-dom";
import { pluralise } from "@/lib/format";
import { FEATURE_LABELS, PLANS, PLAN_ORDER, formatPlanPrice, type Feature } from "@rentorng/core";
import { Wordmark } from "@/components/wordmark";
import { Copyright } from "@/components/copyright";
import { usePageTitle } from "@/lib/title";

/**
 * The public landing page.
 *
 * Copy rule from `.context/02-NAMING-AND-BRAND.md`: say what it does, and never name a
 * continent, a country or a currency. The lead is the promise from the brief — you will not find
 * out about an expiry or an arrear after the fact.
 */

const HIGHLIGHTS = [
  {
    title: "One screen tells you who owes you",
    body: "Overdue, due soon, and collected — as three numbers, not a report you have to build. Every figure opens the list behind it.",
  },
  {
    title: "Reminders that send themselves",
    body: "Rent falling due, rent overdue, tenancy expiring. Set the schedule once and the chase happens without you. Or select any tenants and send by hand.",
  },
  {
    title: "Rent the way it is actually paid",
    body: "Annual, two-year, quarterly or monthly. Paid up front, part paid, or paid in cash across four instalments. Agency, legal and service charges billed separately.",
  },
  {
    title: "Every payment gets a receipt",
    body: "Record cash at the door or a transfer from the bank, allocate it across invoices, and hand over a numbered receipt on the spot.",
  },
  {
    title: "Your landlords stop calling",
    body: "Owner statements showing what was collected, what you deducted, and what is due — without opening a spreadsheet.",
  },
  {
    title: "Proof you chased",
    body: "Every reminder is logged with the exact words the tenant received. Months later, in a dispute, you can show it.",
  },
];

export function LandingPage() {
  usePageTitle("Property management for letting agents");
  return (
    <div className="bg-white">
      <header className="border-b border-ink-200">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/product" className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="" width={32} height={32} className="rounded-lg" />
            <Wordmark className="text-lg" />
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            <a href="#features" className="hidden text-ink-600 hover:text-ink-900 sm:block">
              Features
            </a>
            <a href="#pricing" className="hidden text-ink-600 hover:text-ink-900 sm:block">
              Pricing
            </a>
            <Link to="/sign-in" className="text-ink-600 hover:text-ink-900">
              Sign in
            </Link>
            <Link to="/sign-up" className="btn btn-primary">
              Start free
            </Link>
          </nav>
        </div>
      </header>

      <section className="border-b border-ink-200 bg-brand-900 text-white">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:py-24">
          <p className="mb-3 text-sm font-medium uppercase tracking-widest text-brand-300">Property management</p>
          <h1 className="max-w-3xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            Never miss a renewal or an arrear again.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-brand-100">
            RentorNG runs the portfolio you currently keep in a spreadsheet and a phone. It shows you every overdue
            payment and every upcoming expiry on one screen — and chases them for you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/sign-up" className="btn bg-white text-brand-900 hover:bg-brand-50">
              Start free — no card
            </Link>
            <Link to="/sign-in" className="btn border-brand-400 text-white hover:bg-brand-800">
              Sign in
            </Link>
          </div>
          <p className="mt-4 text-sm text-brand-300">
            Free for up to 25 units. Two weeks of everything, then it drops to free — not to a wall.
          </p>
        </div>
      </section>

      <section id="features" className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-2xl font-semibold">What it does</h2>
        <p className="mt-2 max-w-2xl text-ink-600">
          Built around the four questions a manager asks every morning: who owes me, whose rent expires soon, which
          units are empty, and what do I owe my landlords.
        </p>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {HIGHLIGHTS.map((item) => (
            <div key={item.title} className="card p-5">
              <h3 className="text-base font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-ink-200 bg-ink-50">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <h2 className="text-2xl font-semibold">Take it into the field</h2>
          <p className="mt-2 max-w-2xl text-ink-600">
            The mobile app carries the same dashboard, records a payment and issues a receipt on the spot, and captures
            an inspection room by room — including where there is no signal. It is included from the Growth plan up.
          </p>
        </div>
      </section>

      <section id="pricing" className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-2xl font-semibold">Pricing</h2>
        <p className="mt-2 max-w-2xl text-ink-600">
          Priced by managed units, because that is what grows. Staff logins are not metered — an agency sharing one
          password is an agency with no audit trail.
        </p>

        <div className="mt-8 grid gap-5 lg:grid-cols-4">
          {PLAN_ORDER.map((code) => {
            const plan = PLANS[code];
            return (
              <div key={code} className={`card flex flex-col p-5 ${plan.featured ? "border-brand-600 ring-1 ring-brand-600" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-lg font-semibold">{plan.name}</h3>
                  {plan.featured ? (
                    <span className="rounded-full bg-brand-600 px-2 py-0.5 text-xs text-white">Most agencies</span>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-ink-600">{plan.blurb}</p>
                <p className="tabular mt-4 text-2xl font-semibold">{formatPlanPrice(code)}</p>
                <p className="mt-1 text-xs text-ink-500">
                  {/* Pluralised from the table rather than given a bare "s": an allowance of one
                      reads "Up to 1 units" otherwise, on the page where somebody is deciding
                      whether this product is careful enough to trust with their book. */}
                  {Number.isFinite(plan.limits.units) ? `Up to ${pluralise(plan.limits.units, "unit")}` : "Unlimited units"} ·{" "}
                  {Number.isFinite(plan.limits.staff) ? pluralise(plan.limits.staff, "staff login") : "unlimited staff logins"}
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-ink-700">
                  {(Object.keys(FEATURE_LABELS) as Feature[])
                    .filter((feature) => plan.features.includes(feature))
                    .slice(0, 8)
                    .map((feature) => (
                      <li key={feature} className="flex gap-2">
                        <span className="text-brand-600">✓</span>
                        <span>{FEATURE_LABELS[feature]}</span>
                      </li>
                    ))}
                </ul>
                <div className="mt-5 pt-2">
                  <Link to="/sign-up" className={`btn w-full ${plan.featured ? "btn-primary" : "btn-secondary"}`}>
                    {plan.monthly === 0 ? "Start free" : plan.monthly < 0 ? "Talk to us" : `Choose ${plan.name}`}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-ink-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-8 text-sm text-ink-500">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="" width={22} height={22} className="rounded" />
            {/* Ink, not white: this footer is on white, and the light tone left the name invisible
                with only the dot showing. */}
            <Wordmark />
          </div>
          <p>Properties with ease.</p>
          <Copyright className="w-full text-center sm:w-auto sm:text-right" />
        </div>
      </footer>
    </div>
  );
}
